import {
  formatMentionTypeLabel,
  normalizeMentionTypeKey,
} from '../agent-chat-module/components/gpt-chat-input/mention-picker.constants';
import type {
  MentionSearchType,
  MentionsSearchApiResponse,
} from '../agent-chat-module/types/mentions.service.type';
import { MENTION_SEARCH_TYPES_PARAM } from '../agent-chat-module/types/mentions.service.type';
import { formatMentionDisplayName } from './mention-display-name';

export type MentionPickerItem = {
  id: string;
  type: MentionSearchType;
  title: string;
  body: string;
  subtitle?: string;
  status?: string;
  iconUrl?: string | null;
};

export type MentionCategoryDescriptor = {
  type: MentionSearchType;
  label: string;
  count: number;
};

export type MentionSearchResult = {
  items: MentionPickerItem[];
  categories: MentionCategoryDescriptor[];
};

type RawMentionRecord = Record<string, unknown>;

const META_DATA_KEYS = new Set([
  'items',
  'results',
  'mentions',
  'types',
  'available_types',
  'categories',
  'count',
  'total',
  'limit',
  'offset',
]);

function asRecord(value: unknown): RawMentionRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RawMentionRecord)
    : null;
}

function pickString(record: RawMentionRecord, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function resolveTitle(record: RawMentionRecord): string {
  const name = pickString(record, ['name']);
  if (name) return formatMentionDisplayName(name);

  const title = pickString(record, ['title', 'label']);
  return title;
}

function normalizeItem(raw: unknown, fallbackType?: MentionSearchType): MentionPickerItem | null {
  const record = asRecord(raw);
  if (!record) return null;

  const type =
    normalizeMentionTypeKey(record.type ?? record.mention_type ?? record.kind) ?? fallbackType;
  if (!type) return null;

  const id = pickString(record, ['id', 'item_id', 'mention_id', 'skill_id', 'prompt_id']);
  const title = resolveTitle(record);
  const body = pickString(record, [
    'description',
    'body',
    'content',
    'text',
    'prompt',
    'instruction',
    'instructions',
  ]);

  if (!id || !title) return null;

  const status = pickString(record, ['status', 'connection_status', 'state']);
  const iconUrl = record.iconUrl;
  const iconUrlValue =
    typeof iconUrl === 'string' ? iconUrl : iconUrl === null ? null : undefined;

  return {
    id,
    type,
    title,
    body,
    ...(iconUrlValue !== undefined ? { iconUrl: iconUrlValue } : {}),
    ...(status ? { status } : {}),
  };
}

function normalizeCategoryDescriptor(raw: unknown): MentionCategoryDescriptor | null {
  if (typeof raw === 'string') {
    const type = normalizeMentionTypeKey(raw);
    if (!type) return null;
    return { type, label: formatMentionTypeLabel(type), count: 0 };
  }

  const record = asRecord(raw);
  if (!record) return null;

  const type = normalizeMentionTypeKey(
    record.type ?? record.id ?? record.key ?? record.name ?? record.label
  );
  if (!type) return null;

  const label =
    pickString(record, ['label', 'title', 'name', 'display_name']) || formatMentionTypeLabel(type);
  const countValue = record.count ?? record.total;
  const count = typeof countValue === 'number' ? countValue : 0;

  return { type, label, count };
}

function extractExplicitCategories(dataRecord: RawMentionRecord | null): MentionCategoryDescriptor[] {
  if (!dataRecord) return [];

  const buckets = [dataRecord.types, dataRecord.available_types, dataRecord.categories];
  for (const bucket of buckets) {
    if (!Array.isArray(bucket)) continue;
    const categories = bucket
      .map((entry) => normalizeCategoryDescriptor(entry))
      .filter((entry): entry is MentionCategoryDescriptor => entry != null);
    if (categories.length > 0) return categories;
  }

  return [];
}

function extractGroupedItems(dataRecord: RawMentionRecord): {
  items: unknown[];
  groupTypes: MentionSearchType[];
} {
  const items: unknown[] = [];
  const groupTypes: MentionSearchType[] = [];

  for (const [key, value] of Object.entries(dataRecord)) {
    if (META_DATA_KEYS.has(key) || !Array.isArray(value) || value.length === 0) continue;

    const type = normalizeMentionTypeKey(key);
    if (!type) continue;

    groupTypes.push(type);
    for (const entry of value) {
      const record = asRecord(entry);
      items.push(record ? { ...record, type } : { type, value: entry });
    }
  }

  return { items, groupTypes };
}

function extractRawItems(response: MentionsSearchApiResponse): {
  items: unknown[];
  explicitCategories: MentionCategoryDescriptor[];
  groupTypes: MentionSearchType[];
} {
  if (Array.isArray(response)) {
    return { items: response, explicitCategories: [], groupTypes: [] };
  }

  const data = response.data;
  if (Array.isArray(data)) {
    return { items: data, explicitCategories: [], groupTypes: [] };
  }

  const dataRecord = asRecord(data);
  const explicitCategories = extractExplicitCategories(dataRecord);

  if (!dataRecord) {
    return {
      items: response.items ?? response.results ?? [],
      explicitCategories,
      groupTypes: [],
    };
  }

  const nestedItems = dataRecord.items ?? dataRecord.results ?? dataRecord.mentions;
  if (Array.isArray(nestedItems)) {
    return { items: nestedItems, explicitCategories, groupTypes: [] };
  }

  const grouped = extractGroupedItems(dataRecord);
  if (grouped.items.length > 0) {
    return { items: grouped.items, explicitCategories, groupTypes: grouped.groupTypes };
  }

  return { items: [], explicitCategories, groupTypes: [] };
}

function buildCategories(
  explicitCategories: MentionCategoryDescriptor[],
  _groupTypes: MentionSearchType[],
  items: MentionPickerItem[]
): MentionCategoryDescriptor[] {
  const counts = new Map<MentionSearchType, number>();
  for (const item of items) {
    const type = normalizeMentionTypeKey(item.type);
    if (type) counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  const explicitByType = new Map(
    explicitCategories.map((category) => [
      normalizeMentionTypeKey(category.type) ?? category.type,
      category,
    ])
  );

  return MENTION_SEARCH_TYPES_PARAM.map((type) => {
    const normalized = normalizeMentionTypeKey(type) ?? type;
    const explicit = explicitByType.get(normalized);
    return {
      type: normalized,
      label: explicit?.label ?? formatMentionTypeLabel(normalized),
      count: counts.get(normalized) ?? explicit?.count ?? 0,
    };
  });
}

export function getDefaultMentionCategories(): MentionCategoryDescriptor[] {
  return MENTION_SEARCH_TYPES_PARAM.map((type) => ({
    type: normalizeMentionTypeKey(type) ?? type,
    label: formatMentionTypeLabel(type),
    count: 0,
  }));
}

export function mergeWithDefaultMentionCategories(
  categories: MentionCategoryDescriptor[]
): MentionCategoryDescriptor[] {
  const defaults = getDefaultMentionCategories();
  const byType = new Map(
    categories.map((category) => [
      normalizeMentionTypeKey(category.type) ?? category.type,
      category,
    ])
  );

  return defaults.map((defaultCategory) => {
    const key = normalizeMentionTypeKey(defaultCategory.type) ?? defaultCategory.type;
    const existing = byType.get(key);
    if (!existing) return defaultCategory;

    return {
      ...defaultCategory,
      ...existing,
      label: existing.label || defaultCategory.label,
    };
  });
}

export function normalizeMentionSearchResponse(
  response: MentionsSearchApiResponse
): MentionSearchResult {
  if (!Array.isArray(response) && response?.success === false) {
    throw new Error(
      typeof response.message === 'string' ? response.message : 'Failed to load mentions'
    );
  }

  const { items: rawItems, explicitCategories, groupTypes } = extractRawItems(response);
  const seen = new Set<string>();
  const items: MentionPickerItem[] = [];

  for (const raw of rawItems) {
    const item = normalizeItem(raw);
    if (!item) continue;

    const key = `${item.type}:${item.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(item);
  }

  return {
    items,
    categories: buildCategories(explicitCategories, groupTypes, items),
  };
}
